/**
 * `isProbePath` — deterministic classifier for request paths that look
 * like a security/vuln probe rather than legitimate page reads.
 *
 * The daily rollup pipeline (`agent_daily_rollup`) stores a `top_paths`
 * list per (identity, day) regardless of what the path was. That list
 * surfaces in two merchant-facing surfaces:
 *
 *   - the readiness Journey drawer (JourneyDrawer.tsx)
 *   - the per-agent detail page (AgentDetail.tsx)
 *   - the overview response's `funnel.by_agent[].top_paths`
 *
 * Without this filter, a merchant inspecting "ClaudeBot" sees it fetched
 * `/pricing` 36 times AND `/.env.local` 8 times side-by-side — framing
 * every page load as if an AI assistant were legitimately reading them.
 * Real ClaudeBot does not request `/.env.local`. Those requests are
 * from someone using ClaudeBot's name (impersonation / spoofing) OR
 * from a different client that reused the UA string.
 *
 * The classifier is DETERMINISTIC, CONSERVATIVE, and PURE. It returns
 * `true` only for paths that match a bounded set of attack / probe
 * patterns: hidden config/secret files (dotfiles), WordPress login on
 * non-WP sites (we don't gate here — callers decide), framework debug
 * endpoints, path traversal escapes, and well-known credential/secret
 * filenames. Legitimate paths — product pages, docs, blog posts — ARE
 * NOT touched by this filter.
 *
 * NEVER throws. NEVER consults network. NEVER reads config.
 */
/**
 * Return true when the request path looks like a security probe rather
 * than a legitimate page read.
 *
 * Match classes:
 *
 *   1. Dotfiles / hidden files anywhere in the path — `.env`,
 *      `.env.local`, `.env.production`, `.git/config`, `.aws/config`,
 *      `.ssh/*`, `.zshrc`, `.bash_history`, `.htaccess`, `.DS_Store`.
 *   2. Framework internals that should never be fetched by a user agent
 *      navigating normally — `/__fs/`, `/@fs/`, `/__vite/`,
 *      `/__vite_rsc*`, `/__next/image?...` style debug endpoints,
 *      `/__webpack_hmr`, `/__coverage__`, `/_next/data/__dev__`.
 *   3. Path traversal — `../`, `%2e%2e%2f`, `%2f%2f`, `..%2f`.
 *   4. Linux pseudo-filesystem — `/proc/self/environ`, `/proc/self/*`.
 *   5. Well-known credential/secret file names anywhere — `secrets.*`,
 *      `credentials.*`, `id_rsa`, `id_dsa`, `wallet.dat`, `php_info.php`,
 *      `info.php`, `phpinfo.php`.
 *   6. Admin / login probes aimed at the wrong CMS — `wp-login.php`,
 *      `wp-admin.php`, `/wp-admin/` on non-WordPress sites. The caller
 *      enables this when `site.detected_platform` is NOT WordPress.
 *      (Default off — the filter accepts the WordPress case as unknown
 *      and matches `wp-login.php` always; see below.)
 *   7. Dockerfile / docker-compose at the root — `Dockerfile`,
 *      `docker-compose.y{a,}ml`.
 *   8. Admin / GraphQL probes — `/v1/graphql`, `/graphql.php`,
 *      `/.git`, `/.svn`, `/.hg`, `/CVS/`.
 *   9. Server-side-rendering file-system escapes — `%2e%2e/`,
 *      `%2fproc%2f`, `%00`, mixed-case `%2E%2E`.
 *  10. PHP file requests on paths outside well-known PHP endpoints —
 *      conservative: match `.php` anywhere EXCEPT when it is part of
 *      a `wp-*.php` path (which the caller may want to see on a
 *      WordPress site and which rule 6 already covers).
 *
 * All matches are case-INSENSITIVE on the host-less path. The input is
 * expected to be a path like `/`, `/pricing`, `/.env`, `/wp-login.php`
 * — if a full URL is passed in, we strip the scheme+host first.
 *
 * `true` means "do NOT show this to the merchant as a top page". The
 * caller still has access to the raw count and surfaces it as a
 * separate "probe_hits" number per agent.
 */
/**
 * Bounded percent-decode. Many real probes URL-encode the signature
 * characters (`.`, `/`) to slip past naive filters — richardfallah.com
 * saw hits like `/settings%2F.env` where the dot that triggers the
 * dotfile rule is behind a `%2F`. We decode in a bounded, SAFE loop:
 *
 *   - At most {@link PERCENT_DECODE_PASSES} passes (defuse `%2525...`
 *     onion-encoded payloads without being a decoder of last resort).
 *   - `decodeURIComponent` throws on malformed sequences; we fall back
 *     to a manual byte-wise decode that treats a malformed `%` as a
 *     literal. The classifier NEVER throws.
 *   - Returns the original string unchanged when nothing can be decoded.
 *
 * The decoded value is only used for matching — the caller never sees
 * the decoded path.
 */
export const PERCENT_DECODE_PASSES = 2;
/**
 * Bounded, non-throwing percent-decode exported for the ADR-0245 threat
 * classifier (`src/threat-signatures/classify.ts`), which needs the same
 * decoded view the probe-path classifier uses so paths like
 * `/settings%2F.env` are matched both pre- and post-decode. The decode
 * behaviour is unchanged — the function was already pure.
 */
export function safeDecode(path) {
    let current = path;
    for (let i = 0; i < PERCENT_DECODE_PASSES; i++) {
        if (current.indexOf('%') === -1)
            return current;
        let next;
        try {
            next = decodeURIComponent(current);
        }
        catch {
            // Byte-wise decode that tolerates malformed `%` sequences.
            next = current.replace(/%([0-9a-fA-F]{2})/g, (_m, hex) => {
                try {
                    return String.fromCharCode(parseInt(hex, 16));
                }
                catch {
                    return _m;
                }
            });
        }
        if (next === current)
            return current;
        current = next;
    }
    return current;
}
/**
 * Class 5b private keys (security review m1, 2026-10-14). `.key` / `.pem`
 * alone also name Keynote decks and PUBLIC certificates, so only:
 *   1. private-key names anywhere: `privkey*.pem`, `private*.key|pem`,
 *      `server.key|pem`, and PKCS#12 keystores (`*.p12`, `*.pfx`);
 *   2. any `*.key` / `*.pem` at the path root or under a typical secret
 *      directory (`/ssl/`, `/keys/`, `/private/`, `/certs/private/`);
 * never under `/.well-known/`, and never an obvious public cert
 * (`cert.pem`, `fullchain.pem`, `chain.pem`, `ca.pem`, `cacert.pem`,
 * `ca-bundle.pem`). Applied to the lower-cased, decoded path. ONE source:
 * the SQL mirror (`visit-segments-sql.ts`) binds these strings as-is
 * (Postgres AREs and JS share this lookahead syntax).
 */
const PUBLIC_CERT_LOOKAHEAD = '(?!(cert|fullchain|chain|ca|cacert|ca-bundle)\\.pem$)';
export const PRIVATE_KEY_PATH_RE_SOURCES = [
    `^(?!.*/\\.well-known/).*/${PUBLIC_CERT_LOOKAHEAD}(privkey[^/]*\\.pem|private[^/]*\\.(key|pem)|server\\.(key|pem)|[^/]+\\.(p12|pfx))$`,
    `^(?!.*/\\.well-known/)(/|.*/(ssl|keys|private|certs/private)/)${PUBLIC_CERT_LOOKAHEAD}[^/]+\\.(key|pem)$`,
];
const PRIVATE_KEY_PATH_RES = PRIVATE_KEY_PATH_RE_SOURCES.map((src) => new RegExp(src));
/** Serverless / hosting deploy configs (class 5b): never served on purpose. */
const DEPLOY_CONFIG_NAMES = ['serverless.yml', 'serverless.yaml', 'netlify.toml', 'vercel.json'];
/** Known webshell / web-terminal file names (class 5b), universal. */
const WEBSHELL_NAMES = [
    'terminal-xhr.php', 'c99.php', 'r57.php', 'wso.php', 'b374k.php', 'webshell.php', 'alfa.php', 'shell.php', 'cmd.php',
];
export function isProbePath(input, opts) {
    const wordpress = opts?.wordpress === true;
    if (typeof input !== 'string')
        return false;
    if (input.length === 0)
        return false;
    let path = input;
    // Strip scheme+host if a full URL slipped in.
    const scheme = path.indexOf('://');
    if (scheme > 0) {
        const slash = path.indexOf('/', scheme + 3);
        path = slash === -1 ? '/' : path.slice(slash);
    }
    // Drop query / fragment — probes still get caught by their path.
    const q = path.indexOf('?');
    if (q !== -1)
        path = path.slice(0, q);
    const h = path.indexOf('#');
    if (h !== -1)
        path = path.slice(0, h);
    if (path.length === 0)
        return false;
    const lowerRaw = path.toLowerCase();
    const decoded = safeDecode(path).toLowerCase();
    // Match on BOTH the raw lowercased form (so encoded traversal /
    // encoded dotfile patterns still trip) and the decoded form (so
    // `/settings%2F.env` is treated as `/settings/.env`).
    const lower = decoded;
    // Class 3 + 9 — path traversal escapes anywhere in the path. The
    // encoded forms `%2e`, `%2f` and the raw `../` are both covered.
    // We check BOTH the raw lowercased input and the decoded form so
    // that `%2e%2e%2f` is caught even after decoding collapses it to
    // `../`.
    if (lower.includes('../') || lowerRaw.includes('../'))
        return true;
    if (lowerRaw.includes('%2e%2e'))
        return true;
    if (lowerRaw.includes('..%2f'))
        return true;
    if (lowerRaw.includes('%2fproc%2f'))
        return true;
    if (lowerRaw.includes('%00'))
        return true;
    if (lower.includes('\0'))
        return true;
    // Class 11 (founder feedback 2026-10-14, vbout.com impostor rows) —
    // universal probes seen under borrowed crawler names. Every one is a
    // probe on every site, WordPress included:
    //   - a leading percent-encoded slash (`/%2Fapi/config`): a router-
    //     confusion probe; no real link starts with an encoded slash;
    //   - NTFS alternate data streams (`/.env.production::$DATA`): an IIS
    //     source-disclosure probe;
    //   - `/proc/` anywhere (`/api/proc/self/environ`) and the Vite fs
    //     escape `/@fs/` anywhere (`/@fs/proc/self/environ`);
    //   - file-read / console APIs of dev servers (`/api/fs/read`,
    //     `/api/console/api_server`).
    // Random-token paths (`/rit6uuhdqp3tglnag1ef`) are NOT flagged: too
    // easily a real slug.
    if (/^\/%(?:25)?2f/.test(lowerRaw))
        return true;
    if (lower.includes('::$data'))
        return true;
    if (lower.includes('/proc/'))
        return true;
    if (lower.includes('/@fs/'))
        return true;
    if (lower.startsWith('/api/fs/') || lower.startsWith('/api/console/'))
        return true;
    // Class 4 — Linux pseudo-filesystem.
    if (lower.startsWith('/proc/'))
        return true;
    if (lower.startsWith('/sys/'))
        return true;
    if (lower.startsWith('/dev/'))
        return true;
    // Class 4b — CGI / Windows binary probes. These never belong on a
    // storefront or marketing site; a request for `/cgi-bin/*`, a bare
    // `php-cgi.exe` anywhere, or any `.exe` / `.dll` / `.bat` / `.cmd`
    // / `.sh` path is a scanner looking for RCE surfaces (the long-tail
    // php-cgi 7.x RCE, Windows admin shells, Shellshock, etc.).
    if (lower.startsWith('/cgi-bin/') || lower === '/cgi-bin')
        return true;
    if (lower.includes('/php-cgi'))
        return true;
    if (/\.(exe|dll|bat|cmd|sh)(?:[/?]|$)/.test(lower))
        return true;
    // Class 4c — AI/ML server probe. `/model/info` is the ollama / LLM-
    // serving stack's model-list endpoint; a storefront or marketing
    // site has no business serving it. Conservative: only the exact
    // ollama endpoint (not `/v1/models`, which may be a legitimate API
    // path on dev tooling).
    if (lower === '/model/info' || lower.startsWith('/model/info/'))
        return true;
    // Class 2 — framework internals a real browser never fetches on nav.
    if (lower.startsWith('/@fs/'))
        return true;
    if (lower.startsWith('/@vite/'))
        return true;
    if (lower.startsWith('/__fs/'))
        return true;
    if (lower.startsWith('/__vite'))
        return true;
    if (lower.startsWith('/__webpack'))
        return true;
    if (lower.startsWith('/__coverage'))
        return true;
    if (lower.startsWith('/__next/'))
        return true;
    if (lower.startsWith('/__nuxt/'))
        return true;
    if (lower.startsWith('/__pwa/'))
        return true;
    if (lower.startsWith('/_next/data/__'))
        return true;
    if (lower.startsWith('/__inspect'))
        return true;
    if (lower.startsWith('/__debug'))
        return true;
    if (lower.startsWith('/__/'))
        return true;
    // `.map` source-maps are a probe when fetched standalone against
    // production — pair with a dotfile check below.
    // Class 1 — dotfiles anywhere. A path segment that starts with a
    // period is almost always a probe. We split on `/` and check each
    // non-empty segment. The IETF-standard `.well-known` discovery
    // family is exempt (agent-card, security.txt, matter, …).
    for (const segment of lower.split('/')) {
        if (segment.length === 0)
            continue;
        if (segment === '.well-known')
            continue;
        if (segment.startsWith('.'))
            return true;
    }
    // Class 5 — well-known credential/secret file names, anywhere.
    const SECRET_NAMES = [
        'secrets.env', 'secrets.json', 'secrets.yaml', 'secrets.yml', 'secrets.ini', 'secrets.txt',
        'credentials.json', 'credentials.yml', 'credentials.yaml', 'credentials.env',
        'id_rsa', 'id_dsa', 'id_ecdsa', 'id_ed25519',
        'authorized_keys',
        'wallet.dat', 'wallet.json',
        'phpinfo.php', 'info.php', 'php_info.php',
        'web.config',
        'database.yml', 'database.yaml',
        'config.yml', 'config.yaml', 'config.json',
        'settings.py',
        'application.properties',
        'local.properties',
    ];
    const last_slash = lower.lastIndexOf('/');
    const basename = last_slash === -1 ? lower : lower.slice(last_slash + 1);
    if (SECRET_NAMES.includes(basename))
        return true;
    // Class 5b (2026-10-14) — private keys (`PRIVATE_KEY_PATH_RE_SOURCES`:
    // `/key.pem`, `/server.key`, `/ssl/x.pem`, `/cert.p12`), serverless / hosting
    // deploy configs, and known webshell file names (`terminal-xhr.php`,
    // IceCoder's terminal), which are probes even on a WordPress site.
    if (PRIVATE_KEY_PATH_RES.some((re) => re.test(lower)))
        return true;
    if (DEPLOY_CONFIG_NAMES.includes(basename))
        return true;
    if (WEBSHELL_NAMES.includes(basename))
        return true;
    // Class 7 — Dockerfile / docker-compose at any level.
    if (basename === 'dockerfile')
        return true;
    if (basename === 'docker-compose.yml' || basename === 'docker-compose.yaml')
        return true;
    if (basename === 'package-lock.json' && (lower === '/package-lock.json'))
        return true;
    // Class 6 — WordPress admin / login probes anywhere. These are the
    // most frequent probes against non-WordPress sites. Called out even
    // on real WordPress sites, where they are still probing (though a
    // legitimate admin may use them from a browser) — we drop them from
    // top-pages regardless because they are not part of a merchant's
    // store/pricing/content path.
    // On a WordPress site these are its own pages (login, admin, XML-RPC);
    // `/wp-config*` is a secret file everywhere.
    if (!wordpress && lower.includes('/wp-login.php'))
        return true;
    if (!wordpress && lower.includes('/wp-admin'))
        return true;
    if (lower.includes('/wp-config'))
        return true;
    if (!wordpress && lower.includes('/xmlrpc.php'))
        return true;
    // Class 8 — admin / GraphQL / VCS probes.
    if (lower.includes('/graphql.php'))
        return true;
    if (lower === '/graphql' || lower.startsWith('/graphql/'))
        return true;
    if (lower === '/v1/graphql' || lower.startsWith('/v1/graphql/'))
        return true;
    if (lower === '/admin.php' || lower.startsWith('/administrator/'))
        return true;
    if (lower.startsWith('/.git'))
        return true;
    if (lower.startsWith('/.svn'))
        return true;
    if (lower.startsWith('/.hg'))
        return true;
    if (lower.startsWith('/cvs/'))
        return true;
    // Class 10 — bare `.php` anywhere outside the well-known endpoints.
    // Modern storefronts (Shopify, Next.js, Astro, Framer, Wix, Webflow,
    // Squarespace, Vite SPAs) never serve `.php`. A path ending `.php`
    // against them is a probe.
    // Never on a WordPress site (its pages and plugins are `.php`), and
    // never for a sitemap (`/xmlsitemap.php`, `/sitemap_index.php`): many
    // CMSes generate those.
    if (!wordpress && /\.php(?:\/|$)/.test(lower) && !/sitemap[^/]*\.php(?:\/|$)/.test(lower))
        return true;
    // `.env` matches rule 1 (dotfile). `.env.local`, `.env.production`
    // also match rule 1.
    // Account / credential enumeration — Sill's own API endpoints follow
    // the `/v1/...` or `/_sill/...` shapes. A request for
    // `/api/account` or `/api/credentials` against a storefront is a
    // probe.
    if (lower === '/api/account' || lower.startsWith('/api/account/'))
        return true;
    if (lower === '/api/credentials' || lower.startsWith('/api/credentials/'))
        return true;
    if (lower === '/api/debug' || lower.startsWith('/api/debug/'))
        return true;
    return false;
}
/**
 * Return the "kind" of probe for display next to a count. Used by the
 * Journey drawer's impersonation line to say "tried to read secret
 * files 24 times (e.g. /.env, /.aws/config)". The classifier returns
 * one of a small closed set so the UI can colour / group them without
 * extra plumbing.
 */
export function probeKindOf(path, opts) {
    if (!isProbePath(path, opts))
        return null;
    const lowerRaw = path.toLowerCase();
    const lower = safeDecode(path).toLowerCase();
    if (lower.includes('../')
        || lowerRaw.includes('../')
        || lowerRaw.includes('%2e%2e')
        || lowerRaw.includes('..%2f')
        || lowerRaw.includes('%00')
        || lower.includes('\0')) {
        return 'traversal';
    }
    if (lower.includes('/wp-login') || lower.includes('/wp-admin') || lower.includes('/wp-config') || lower.includes('/xmlrpc.php')) {
        return 'wp_probe';
    }
    if (lower.startsWith('/@fs') || lower.startsWith('/@vite') || lower.startsWith('/__')) {
        return 'framework_internal';
    }
    if (lower === '/graphql' || lower.startsWith('/graphql') || lower.includes('/v1/graphql')) {
        return 'graphql';
    }
    // CGI probes BEFORE the generic `.php` fall-through so a request for
    // `/cgi-bin/php-cgi` ends up as `cgi`, not `php`.
    if (lower.startsWith('/cgi-bin') || lower.includes('/php-cgi'))
        return 'cgi';
    if (/\.(exe|dll|bat|cmd|sh)(?:[/?]|$)/.test(lower))
        return 'binary';
    if (/\.php(?:\/|$)/.test(lower))
        return 'php';
    // Dotfile or well-known secret filename — a secret file probe.
    for (const segment of lower.split('/')) {
        if (segment.length === 0)
            continue;
        if (segment.startsWith('.'))
            return 'secret_file';
    }
    if (lower.startsWith('/proc/') || lower.startsWith('/sys/') || lower.startsWith('/dev/')) {
        return 'secret_file';
    }
    if (PRIVATE_KEY_PATH_RES.some((re) => re.test(lower)) || /\.toml$/.test(lower) || lower.includes('/proc/'))
        return 'secret_file';
    if (lower.endsWith('.env') || lower.endsWith('.json') || lower.endsWith('.yml') || lower.endsWith('.yaml') || lower.endsWith('.ini') || lower.endsWith('.txt')) {
        return 'secret_file';
    }
    return 'other';
}
//# sourceMappingURL=probe-paths.js.map